import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { ApiError } from '@/lib/problem-details'

import { SavingsProblemAlert } from './savings-errors'

/**
 * The shell every savings command dialog is built in.
 *
 * It has two faces. Before the click it is a form, with whatever has to be read first above the
 * fields. After a command that produced a result worth stating — a countersignature queued, a
 * posting made, a hold amended rather than added — the form is replaced by that result, because an
 * operator who watches a dialog vanish reads it as "done" whatever a toast says next.
 *
 * A command whose success is unambiguous passes no `outcome` and simply closes, which is the
 * honest thing there: it did what it said.
 */
export interface SavingsActionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: ReactNode
  /** Rendered above the fields — dual-control and till notices belong here. */
  notice?: ReactNode
  children: ReactNode
  /** Present once the command has answered; it replaces the form. */
  outcome?: ReactNode
  confirmLabel: string
  tone?: 'default' | 'danger'
  isPending?: boolean
  /** True while the form is incomplete. */
  disabled?: boolean
  error?: ApiError | null
  onConfirm: () => void
  /** Label for the button that dismisses a settled dialog. */
  closeLabel?: string
}

export function SavingsActionDialog({
  open,
  onOpenChange,
  title,
  description,
  notice,
  children,
  outcome,
  confirmLabel,
  tone = 'default',
  isPending = false,
  disabled = false,
  error = null,
  onConfirm,
  closeLabel = 'Close',
}: SavingsActionDialogProps) {
  const settled = outcome !== undefined && outcome !== null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {settled ? (
            outcome
          ) : (
            <>
              {notice}
              {children}
              {error ? <SavingsProblemAlert error={error} /> : null}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          {settled ? (
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              {closeLabel}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                Cancel
              </Button>
              <Button
                variant={tone === 'danger' ? 'danger' : 'primary'}
                onClick={onConfirm}
                disabled={isPending || disabled}
              >
                {isPending ? 'Working…' : confirmLabel}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
